import { useState } from 'react';

/**
 * Hook personnalisé pour gérer le modal d'ajout de participants
 */
export const useModalForm = () => {
  // États du modal
  const [modalOpen, setModalOpen] = useState(false);
  const [participantType, setParticipantType] = useState('');
  const [modalStep, setModalStep] = useState(0);

  // États pour la gestion des leads
  const [leadFound, setLeadFound] = useState(null);
  const [searchingLead, setSearchingLead] = useState(false);

  // États pour la validation
  const [formErrors, setFormErrors] = useState({});

  // États pour les données de l'accompagnateur
  const [accompanistData, setAccompanistData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '+33', // France par défaut
    phoneNumber: ''
  });

  // États pour les données du futur soldat
  const [soldierData, setSoldierData] = useState({
    email: '',
    confirmEmail: '',
    firstName: '',
    lastName: '',
    countryCode: '+33',
    phoneNumber: ''
  });

  /**
   * Ouvre le modal
   */
  const openModal = () => {
    setModalOpen(true);
  };

  /**
   * Ferme le modal et réinitialise tous les états
   */
  const closeModal = () => {
    setModalOpen(false);
    setParticipantType('');
    setModalStep(0);
    setLeadFound(null);
    setSearchingLead(false);
    setFormErrors({});
    
    // Réinitialiser les données de l'accompagnateur
    setAccompanistData({
      firstName: '',
      lastName: '',
      email: '',
      countryCode: '+33',
      phoneNumber: ''
    });

    // Réinitialiser les données du futur soldat
    setSoldierData({
      email: '',
      confirmEmail: '',
      firstName: '',
      lastName: '',
      countryCode: '+33',
      phoneNumber: ''
    });
  };

  /**
   * Gestionnaire pour les changements de données d'accompagnateur
   * @param {string} field - Le champ à modifier
   * @returns {Function} La fonction de gestion du changement
   */
  const handleAccompanistDataChange = (field) => (event) => {
    const value = event.target.value;
    const newData = {
      ...accompanistData,
      [field]: value
    };
    setAccompanistData(newData);

    // Validation en temps réel pour le champ modifié
    const errors = { ...formErrors };
    
    if (field === 'firstName' || field === 'lastName') {
      if (!value.trim()) {
        errors[field] = field === 'firstName' ? 'Le prénom est requis' : 'Le nom est requis';
      } else {
        delete errors[field];
      }
    } else if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value.trim()) {
        errors[field] = 'L\'email est requis';
      } else if (!emailRegex.test(value)) {
        errors[field] = 'Format d\'email invalide';
      } else {
        delete errors[field];
      }
    } else if (field === 'phoneNumber') {
      if (!value.trim()) {
        errors[field] = 'Le numéro de téléphone est requis';
      } else if (!/^[0-9\s\-().]+$/.test(value)) {
        errors[field] = 'Format de numéro de téléphone invalide';
      } else {
        delete errors[field];
      }
    }

    setFormErrors(errors);
  };

  /**
   * Gestionnaire pour les changements de données de futur soldat
   * @param {string} field - Le champ à modifier
   * @returns {Function} La fonction de gestion du changement
   */
  const handleSoldierDataChange = (field) => (event) => {
    const value = event.target.value;
    const newData = {
      ...soldierData,
      [field]: value
    };
    setSoldierData(newData);

    // Validation en temps réel pour le champ modifié
    const errors = { ...formErrors };
    
    if (field === 'firstName' || field === 'lastName') {
      if (!value.trim()) {
        errors[field] = field === 'firstName' ? 'Le prénom est requis' : 'Le nom est requis';
      } else {
        delete errors[field];
      }
    } else if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value.trim()) {
        errors[field] = 'L\'email est requis';
      } else if (!emailRegex.test(value)) {
        errors[field] = 'Format d\'email invalide';
      } else {
        delete errors[field];
      }
    } else if (field === 'confirmEmail') {
      if (!value.trim()) {
        errors[field] = 'La confirmation de l\'email est requise';
      } else if (value !== newData.email) {
        errors[field] = 'Les emails ne correspondent pas';
      } else {
        delete errors[field];
      }
    } else if (field === 'phoneNumber') {
      if (!value.trim()) {
        errors[field] = 'Le numéro de téléphone est requis';
      } else if (!/^[0-9\s\-().]+$/.test(value)) {
        errors[field] = 'Format de numéro de téléphone invalide';
      } else {
        delete errors[field];
      }
    }

    // Validation croisée pour la confirmation d'email
    if (field === 'email' && newData.confirmEmail && newData.confirmEmail !== value) {
      errors.confirmEmail = 'Les emails ne correspondent pas';
    } else if (field === 'email' && newData.confirmEmail && newData.confirmEmail === value) {
      delete errors.confirmEmail;
    }

    setFormErrors(errors);
  };

  /**
   * Passe à l'étape suivante
   */
  const nextStep = () => {
    setModalStep(prev => prev + 1);
  };

  /**
   * Retourne à l'étape précédente
   */
  const previousStep = () => {
    setModalStep(prev => Math.max(0, prev - 1));
  };

  /**
   * Va directement à une étape spécifique
   * @param {number} step - L'étape à atteindre
   */
  const goToStep = (step) => {
    setModalStep(step);
  };

  /**
   * Défini le type de participant et passe à l'étape suivante
   * @param {string} type - Le type de participant
   */
  const selectParticipantType = (type) => {
    setParticipantType(type);
    nextStep();
  };

  /**
   * Met à jour les erreurs de formulaire
   * @param {Object} errors - Les erreurs à définir
   */
  const setErrors = (errors) => {
    setFormErrors(errors);
  };

  /**
   * Met à jour l'état de recherche de lead
   * @param {boolean} searching - État de recherche
   */
  const setLeadSearching = (searching) => {
    setSearchingLead(searching);
  };

  /**
   * Met à jour le lead trouvé
   * @param {Object|null} lead - Le lead trouvé ou null
   */
  const setFoundLead = (lead) => {
    setLeadFound(lead);
  };

  return {
    // États
    modalOpen,
    participantType,
    modalStep,
    leadFound,
    searchingLead,
    formErrors,
    accompanistData,
    soldierData,

    // Actions
    openModal,
    closeModal,
    nextStep,
    previousStep,
    goToStep,
    selectParticipantType,
    setErrors,
    setLeadSearching,
    setFoundLead,
    handleAccompanistDataChange,
    handleSoldierDataChange
  };
};